import { derived, get, writable } from "svelte/store";
import type { AreaData } from "@workadventure/map-editor";
import { gameManager } from "../Phaser/Game/GameManager";
import { localUserStore } from "../Connection/LocalUserStore";

/**
 * Who owns the audio room the player is currently standing in.
 *
 * WorkAdventure already carries a `livekitRoomAdminTag` on every LiveKit area - the map
 * editor has a field for it, the map storage accepts it - but nothing reads it yet. This
 * is where it starts meaning something: whoever holds that tag is the owner of the room
 * and may throw people out of it.
 *
 * Using the existing field rather than inventing a property type is deliberate. Area
 * properties are a discriminated union validated by the map storage, which runs the
 * official image here; an unknown `type` would be rejected on save.
 */
export interface SalaAtual {
    area: AreaData;
    /** Tag that owns this room. Empty means nobody owns it by tag. */
    adminTag: string;
    /** uuid of whoever claimed this room in-game, if the area carries a personal area property. */
    ownerId?: string;
}

/** The LiveKit area the player is inside, or undefined when out in the open. */
export const salaAtualStore = writable<SalaAtual | undefined>(undefined);

/**
 * True when the player owns the room they are standing in.
 *
 * Two ways to own a room, because this deployment logs everyone in anonymously:
 *
 *  - by tag (livekitRoomAdminTag). The proper one, and dead until people have identities:
 *    tags come from the auth token, and with DISABLE_ANONYMOUS=false nobody carries any.
 *  - by claim (personalAreaPropertyData.ownerId). Works today, because even an anonymous
 *    user has a uuid. The catch is that the uuid lives in localStorage, so clearing the
 *    browser loses the room.
 *
 * Both are checked, so the day a login shows up the tag path starts working on its own,
 * with nothing to migrate.
 */
export const souDonoDaSalaStore = derived(salaAtualStore, ($sala) => {
    if (!$sala) return false;

    const tag = $sala.adminTag?.trim();
    if (tag && tagsDoUsuario().includes(tag)) return true;

    const meuUuid = localUserStore.getLocalUser()?.uuid;
    return !!$sala.ownerId && !!meuUuid && $sala.ownerId === meuUuid;
});

function tagsDoUsuario(): string[] {
    try {
        return gameManager.getCurrentGameScene().connection?.getAllTags() ?? [];
    } catch {
        // Called before the scene is up - nobody owns anything yet.
        return [];
    }
}

/**
 * Where to walk someone who has just been thrown out: one tile past the closest edge of
 * the room, so they land in whatever is outside instead of at a fixed spot that might be
 * on the far side of the map.
 *
 * moveTo() pathfinds and takes the nearest reachable tile, so a wall on that side only
 * shifts the landing spot - it does not strand the person inside.
 */
export function saidaMaisProxima(area: AreaData, x: number, y: number): { x: number; y: number } {
    const MARGEM = 48; // 1,5 tile: o suficiente pra sair da area e nao encostar na borda
    const dEsq = x - area.x;
    const dDir = area.x + area.width - x;
    const dCima = y - area.y;
    const dBaixo = area.y + area.height - y;
    const menor = Math.min(dEsq, dDir, dCima, dBaixo);

    if (menor === dEsq) return { x: area.x - MARGEM, y };
    if (menor === dDir) return { x: area.x + area.width + MARGEM, y };
    if (menor === dCima) return { x, y: area.y - MARGEM };
    return { x, y: area.y + area.height + MARGEM };
}

/** The room the player is in, read once (not reactive) - for use inside event handlers. */
export const salaAtual = () => get(salaAtualStore);
